import { profileState } from "@/atoms/user/profile-state";
import { ButtonColor } from "@/common/enum/button-color";
import { PaginationSize } from "@/common/enum/pagination-size";
import { noticeService } from "@/services/notice-service";
import { NoticeDetailDto } from "@/types/dto/notice-detail-dto";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Button } from "flowbite-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { AngleLeftOutlineIcon } from "../icon/AngleLeftlineIcon";
import { AngleRightOutlineIcon } from "../icon/AngleRightlineIcon";
import { H4 } from "../typography/H4";

const MODAL_HIDE_DURATION = 1;
const STORAGE_KEY_PREFIX = "partner-home-notice-modal-hidden-";

export const PartnerHomeNoticeModal = () => {
  const [profile] = useRecoilState(profileState);

  const [allvalidNotices, setAllValidNotices] = useState<NoticeDetailDto[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showModal, setShowModal] = useState(true);

  const handlePrevPage = () => {
    setCurrentIdx((i) => Math.max(0, i - 1));
  };

  const handleNextPage = () => {
    setCurrentIdx((i) => Math.min(allvalidNotices.length - 1, i + 1));
  };

  const fetchAllNotices = async () => {
    try {
      const pageSize = PaginationSize.SIZE_10;
      let page = 1;
      let allNotice: NoticeDetailDto[] = [];

      while (true) {
        const response = await noticeService.getPage({
          page,
          size: pageSize,
          isFiltered: false,
        });
        if (!response.ok) break;

        allNotice = allNotice.concat(response.data.contents);

        if (page >= response.data.totalPage) break;
        page += 1;
      }
      const now = moment().startOf("day");

      const validNotices = allNotice.filter((notice) => {
        const start = moment(notice.popupStartDate).startOf("day");
        const end = moment(notice.popupEndDate).startOf("day");
        return now.isSameOrAfter(start) && now.isSameOrBefore(end);
      });

      const filteredNotices = validNotices.filter((notice) => {
        const key = `${STORAGE_KEY_PREFIX}${profile.id}-${notice.id}`;
        const last = localStorage.getItem(key);
        if (!last) return true;
        const days = moment().diff(moment(last), "days");
        return days > MODAL_HIDE_DURATION;
      });

      setAllValidNotices(filteredNotices);
      setCurrentIdx(0);
    } catch (error) {
      console.log("failed to fetch notice: ", error);
    }
  };

  useEffect(() => {
    if (profile) {
      fetchAllNotices();
    } else {
      return;
    }
  }, [profile]);

  const notShowToday = async () => {
    const now = moment().toISOString();
    allvalidNotices.forEach((notice) => {
      const key = `${STORAGE_KEY_PREFIX}${profile.id}-${notice.id}`;
      localStorage.setItem(key, now);
    });
    setShowModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  if (!showModal || allvalidNotices.length === 0) {
    return null;
  }

  const currentPage = currentIdx + 1;
  const totalPage = allvalidNotices.length;
  const notice = allvalidNotices[currentIdx];

  return (
    <div className="fixed top-10 left-10 z-50 min-w-[416px] min-h-[90vh]">
      <div id="default-modal" tabIndex={-1} className="h-full">
        <div className="relative p-4 w-full max-w-2xl h-full">
          <div className="relative bg-white rounded-lg shadow dark:bg-gray-700 h-full flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
              <span className="break-words whitespace-normal min-w-0 flex-1 text-xl font-semibold ">
                {notice.title}
              </span>
              <button
                type="button"
                className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                onClick={closeModal}
              >
                <svg
                  className="w-3 h-3"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>
            {/* Modal body */}
            <div className="p-4 md:p-5 space-y-4 overflow-y-auto flex-1 h-full">
              <ModalBody notice={notice} />
            </div>
            {/* Modal footer */}
            <div className="flex justify-between items-center p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600">
              <div className="">
                <Button onClick={notShowToday}>오늘 그만 보기</Button>
              </div>
              <Button.Group>
                <Button
                  size="sx"
                  color={currentPage === 1 ? "" : ButtonColor.ORIGIN_GRAY}
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="w-[40px] h-[40px]"
                >
                  <div className="flex items-center mx-[4px]">
                    <AngleLeftOutlineIcon
                      size={20}
                      color={
                        currentPage === 1
                          ? "text-primary-gray"
                          : "text-semantic-text-secondary"
                      }
                    />
                  </div>
                </Button>
                <div className="w-[8px] border-r-[1px] border-r-[#1c375a29]" />
                <Button
                  size="sx"
                  color={
                    currentPage + 1 > totalPage ? "" : ButtonColor.ORIGIN_GRAY
                  }
                  onClick={handleNextPage}
                  disabled={currentPage + 1 > totalPage}
                  className="w-[40px] h-[40px]"
                >
                  <div className="flex items-center mx-[4px]">
                    <AngleRightOutlineIcon
                      size={20}
                      color={
                        currentPage + 1 > totalPage
                          ? "text-primary-gray"
                          : "text-semantic-text-secondary"
                      }
                    />
                  </div>
                </Button>
              </Button.Group>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface Props {
  notice: NoticeDetailDto;
}

const ModalBody: React.FC<Props> = ({ notice }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: true,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          class: "custom-link h-full",
        },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: {
          style: "display: none",
        },
      }),
    ],
    content: notice.content ? JSON.parse(notice.content) : {},
    editable: false,
  });

  useEffect(() => {
    if (editor && notice.content) {
      editor.commands.setContent(JSON.parse(notice.content));
    }
  }, [editor, notice.content]);

  return (
    <div className="parner-home-custom-editor-wrapper h-full">
      <EditorContent editor={editor} className="h-[full]" />
    </div>
  );
};
